import React, { useRef, useEffect, useState, useMemo } from "react";
import { Check, ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import Select from "../ui/Select";
import { DatePicker } from "../ui/DatePicker";
import { cn } from "@/lib/utils";

// ─── Filter Pill ──────────────────────────────────────────────────────────────
// `onRemove` is optional. Every filter-bar caller passes one, so the X used to
// render unconditionally — which put a remove affordance on a pill that could
// not be removed the first time a read-only screen used it. A pill with no
// handler is a label, and shows no X.
export const FilterPill = ({ label, onRemove }) => (
  <div className="flex items-center max-w-full gap-1.5 px-3 py-1 rounded-full text-[0.72rem] font-semibold bg-accent-soft/10 border border-accent-border/30 text-accent-text transition-all duration-200">
    <span className="truncate max-w-[180px]">{label}</span>

    {onRemove && (
      // A button, not a bare icon: this is a control, so it has to be
      // reachable and named. `.claude/rules/frontend-ux.md` — an icon-only
      // control carries an `aria-label`.
      <Button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="p-0 ml-0.5 shrink-0 cursor-pointer bg-transparent rounded-none">
        <X size={10} />
      </Button>
    )}
  </div>
);

// ─── Filter MultiSelect ───────────────────────────────────────────────────────
export const FilterMultiSelect = ({ label, value = [], onChange, options = [], fullWidth = false, open, onToggle, disabled = false, emptyText }) => {
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  const handleToggleOption = (val, optionDisabled) => {
    // A disabled row is inert on click as well as greyed. Relying on the
    // styling alone would let a keyboard or a mis-aimed click select the one
    // value the server is going to refuse.
    if (optionDisabled) return;
    if (selectedValues.includes(val)) {
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      onChange([...selectedValues, val]);
    }
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onChange([]);
  };

  const triggerText = useMemo(() => {
    if (selectedValues.length === 0) return label;
    if (selectedValues.length === 1) {
      return options.find((o) => o.value === selectedValues[0])?.label || label;
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, label]);

  return (
    <div className={cn("relative", fullWidth ? "w-full" : "w-full sm:w-auto sm:min-w-[140px]")}>
      {/* Trigger */}
      {/* `disabled` (Phase D) exists so the read-only checklist builder can show
        what is selected without offering to change it. The trigger stops
        toggling AND the dropdown stops rendering, because a control that
        opens and then refuses every click is worse than one that does not
        open. */}
      <div
        onClick={disabled ? undefined : onToggle}
        aria-disabled={disabled || undefined}
        className={cn(
          "bg-background-tertiary border border-border-primary rounded-lg text-[0.78rem] text-text-primary px-3 py-[7px] flex justify-between items-center gap-2 w-full",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        )}
      >
        <span className="truncate">{triggerText}</span>
        <ChevronDown size={13} className={cn("shrink-0 transition-transform duration-200", open && "rotate-180")} />
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute text-text-primary z-50 mt-1 w-full min-w-[200px] bg-background-secondary border border-border-primary rounded-lg shadow-lg max-h-[40vh] overflow-y-auto overflow-x-hidden custom-scrollbar py-1">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border-primary/60 text-xs">
            <span className="font-semibold text-[11px] text-text-secondary uppercase tracking-wider">{label}</span>
            {selectedValues.length > 0 && (
              <Button onClick={handleClear} className="p-0 rounded-none text-[11px] text-tone-rose hover:underline cursor-pointer">
                Clear
              </Button>
            )}
          </div>
          {options.length === 0 && <div className="px-3 py-2.5 text-[11px] text-text-secondary">{emptyText || "Nothing to choose from."}</div>}
          {options.map((o) => {
            const isChecked = selectedValues.includes(o.value);
            // Per-option `disabled` + `hint`: an option the caller knows the
            // server would refuse is shown and explained rather than dropped.
            // Hiding it makes the row the user is hunting for simply absent,
            // which reads as "no such thing" — a different and more confusing
            // claim than the reason.
            const optionDisabled = Boolean(o.disabled);
            return (
              <div
                key={o.value}
                onClick={() => handleToggleOption(o.value, optionDisabled)}
                aria-disabled={optionDisabled || undefined}
                title={o.hint || undefined}
                className={cn(
                  "px-3 py-2 text-xs flex items-center gap-2.5 select-none transition-colors",
                  optionDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-background-tertiary",
                )}
              >
                <div
                  className={cn(
                    "w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0",
                    isChecked ? "bg-tone-emerald border-tone-emerald text-white" : "border-border-primary bg-background-form/40",
                  )}
                >
                  {isChecked && <Check size={11} strokeWidth={3} />}
                </div>
                <span className="flex-1 min-w-0">
                  <span className={cn("truncate block", isChecked && "font-semibold text-text-primary")}>{o.label}</span>
                  {o.hint && <span className="block truncate text-[10px] text-text-secondary">{o.hint}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Filter Select ────────────────────────────────────────────────────────────
export const FilterSelect = ({ label, value, onChange, options, fullWidth = false, searchable = false }) => {
  // A leading entry that reads as the field's own label, wired to clear the
  // value — the same "reset" row the old hand-rolled dropdown put above the
  // real options.
  const selectOptions = useMemo(() => [{ label, value: "" }, ...(options || [])], [label, options]);

  return (
    // `Select` portals its option list to `document.body`, outside whatever
    // panel this sits in (the Filter dropdown, most often). A parent that
    // closes itself on an "outside" mousedown — Filter's does — reads a click
    // on a portaled option as outside and closes before onChange fires.
    // Stopping the mousedown here, not at each caller, is what keeps every
    // FilterSelect safe regardless of how it is wired in (the switch below,
    // or a page's own `renderFilter` override).
    <div className={cn(fullWidth ? "w-full" : "w-full sm:w-auto sm:min-w-[140px]")} onMouseDown={(e) => e.stopPropagation()}>
      <Select value={value} onChange={onChange} options={selectOptions} placeholder={label} searchable={searchable} className="h-9 bg-background-tertiary px-3 text-[0.78rem]" />
    </div>
  );
};

// ─── Main Filter ──────────────────────────────────────────────────────────────
export const Filter = ({
  filters = [],
  activeFilters = [],
  onClearAll,
  showFilters,
  onToggle,
  renderFilter,
  renderActiveFilter,
  hideTrigger = false,
  noGlass = false,
  dropdownClassName = "",
}) => {
  const containerRef = useRef(null);
  const [openKey, setOpenKey] = useState(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        if (showFilters) onToggle?.();
        setOpenKey(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters, onToggle]);

  // Close dropdown list when filter panel closes
  useEffect(() => {
    if (!showFilters) setOpenKey(null);
  }, [showFilters]);

  const renderFilterItem = (f) => {
    if (renderFilter) {
      return renderFilter({
        ...f,
        open: openKey === f.key,
        onToggle: () => setOpenKey((prev) => (prev === f.key ? null : f.key)),
      });
    }

    switch (f.type) {
      case "multiselect":
        return (
          <FilterMultiSelect
            key={f.key}
            label={f.label}
            value={f.value}
            onChange={f.onChange}
            options={f.options || []}
            fullWidth={f.fullWidth}
            open={openKey === f.key}
            onToggle={() => setOpenKey((prev) => (prev === f.key ? null : f.key))}
          />
        );

      case "select":
        return <FilterSelect key={f.key} label={f.label} value={f.value} onChange={f.onChange} options={f.options || []} fullWidth={f.fullWidth} />;

      case "input":
        return (
          <Input
            key={f.key}
            type={f.inputType || "text"}
            placeholder={f.label}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            className="bg-background-tertiary/30 px-3 py-[7px] text-xs outline-none focus:outline-none focus:ring-1 focus:ring-accent-border focus-visible:border-border-primary focus-visible:ring-1 focus-visible:ring-accent-border"
          />
        );

      case "date":
        return (
          <div key={f.key} className="w-full" onMouseDown={(e) => e.stopPropagation()}>
            <DatePicker value={f.value} onChange={f.onChange} placeholder={f.placeholder || "Pick a date"} buttonClassName="w-full h-9 text-xs" />
          </div>
        );

      case "row":
        return (
          <div key={f.key} className="flex gap-2 w-full">
            {f.children.map((child) => (
              <div key={child.key} className="flex-1 min-w-0">
                {child.type === "input" && (
                  <Input
                    placeholder={child.label}
                    value={child.value}
                    onChange={(e) => child.onChange(e.target.value)}
                    className="bg-background-tertiary px-3 py-[7px] text-xs outline-none focus:outline-none focus:ring-1 focus:ring-accent-border focus-visible:border-border-primary focus-visible:ring-1 focus-visible:ring-accent-border"
                  />
                )}
                {child.type === "date" && (
                  <div onMouseDown={(e) => e.stopPropagation()}>
                    <DatePicker
                      value={child.value}
                      onChange={child.onChange}
                      minDate={child.minDate}
                      placeholder={child.placeholder || "Pick a date"}
                      buttonClassName="w-full h-9 text-xs bg-background-tertiary hover:bg-background-tertiary border border-border-primary rounded-lg px-3 py-[7px] text-xs text-text-primary"
                      calendarClassName="bg-background-tertiary border border-border-primary rounded-lg px-3 py-[7px] text-xs text-text-primary"
                    />
                  </div>
                )}
                {child.type === "select" && (
                  <div onMouseDown={(e) => e.stopPropagation()}>
                    <Select
                      value={child.value}
                      onChange={child.onChange}
                      options={[{ label: child.label, value: "" }, ...(child.options || [])]}
                      className="h-9 bg-background-tertiary px-3 text-xs"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      default:
        return <div key={f.key}>{f.label}</div>;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger Button */}
      {!hideTrigger && (
        <Button
          onClick={onToggle}
          aria-label="Filters"
          className={cn(
            "relative flex items-center justify-center",
            "w-9 h-9 rounded-full",
            "text-text-primary bg-background-secondary border border-border-primary",
            !noGlass && "shadow-glass",
            "transition-all duration-200 hover:scale-105 active:scale-95 p-0",
          )}
        >
          <SlidersHorizontal size={13} strokeWidth={1.8} />
          {activeFilters.length > 0 && (
            <span className="absolute -top-1 -right-1 z-20 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[0.6rem] font-bold text-white bg-button-primary border-2 border-background-primary shadow-xs">
              {activeFilters.length}
            </span>
          )}
        </Button>
      )}

      {/* Dropdown Panel */}
      {showFilters && (
        <>
          {/* Mobile backdrop */}
          <div className="fixed inset-0 z-40 bg-black/20 sm:hidden" onClick={onToggle} />

          <div
            style={{ zoom: 0.95 }}
            className={cn(
              "z-50",
              "fixed bottom-0 left-0 right-0 rounded-t-2xl",
              "sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-12 sm:rounded-2xl sm:w-[300px] sm:max-w-[90vw]",
              "md:w-[320px]",
              "py-4 px-3 flex flex-col gap-3",
              "bg-background-secondary border border-border-primary shadow-xl",
              "animate-in slide-in-from-bottom-4 duration-200 sm:animate-in sm:fade-in sm:zoom-in-95 sm:duration-150",
              dropdownClassName,
            )}
          >
            {/* Mobile drag handle */}
            <div className="flex justify-center sm:hidden -mt-1 mb-1">
              <div className="w-10 h-1 rounded-full bg-border-primary" />
            </div>

            {/* Panel header (mobile) */}
            <div className="flex items-center justify-between sm:hidden mb-1">
              <span className="text-sm font-semibold text-text-primary">Filters</span>
              <Button onClick={onToggle} className="p-1 rounded-lg hover:bg-background-tertiary transition-colors">
                <X size={16} className="text-text-muted" />
              </Button>
            </div>

            {/* Filter Controls */}
            <div className="flex flex-col gap-2.5">
              {filters.map((f) => (
                <div key={f.key} className="w-full">
                  {renderFilterItem(f)}
                </div>
              ))}
            </div>

            {/* Active Filter Pills */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-border-primary">
                <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                  {activeFilters.map((f) => (renderActiveFilter ? renderActiveFilter(f) : <span key={f.key}>{f.label}</span>))}
                </div>
                <Button onClick={onClearAll} className="shrink-0 text-xs font-medium text-red-400 hover:opacity-70 p-0">
                  Clear all
                </Button>
              </div>
            )}

            {/* Safe area spacer on mobile */}
            <div className="h-safe-area-inset-bottom sm:hidden" />
          </div>
        </>
      )}
    </div>
  );
};
